
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram } from "lucide-react";
import { FaXTwitter } from "react-icons/fa6";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ContactFormModal } from "@/components/ContactFormModal";
import { PrivacyPolicyModal } from "@/components/PrivacyPolicyModal";
import { CookiePolicyModal } from "@/components/CookiePolicyModal";
import { DisclaimerModal } from "@/components/DisclaimerModal";
import { TermsOfServiceModal } from "@/components/TermsOfServiceModal";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";

export function BusinessFooter() {
  const currentYear = new Date().getFullYear();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [popularCities, setPopularCities] = useState<string[]>([]);
  const { createCitySlug } = useDealerUtils();
  
  // Fetch popular cities for the footer
  useEffect(() => {
    // Define popular cities in Italy
    const cities = [
      "Roma", "Milano", "Napoli", "Torino", 
      "Palermo", "Genova", "Bologna", "Firenze", 
      "Bari", "Catania", "Venezia", "Verona"
    ];
    setPopularCities(cities);
  }, []);
  
  const handleCityClick = (city: string, e: React.MouseEvent) => {
    e.preventDefault();
    const citySlug = createCitySlug(city);
    // Use direct window location to ensure full page reload and state reset
    window.location.href = `/compro-oro/${citySlug}`;
  };
  
  return (
    <footer className="bg-card border-t">
      <div className="container mx-auto py-6 px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-foreground">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gold via-gold-light to-gold-dark dark:from-gold-light dark:via-gold dark:to-gold-dark">
                Gildy
              </span>
            </h3>
            <p className="text-sm text-muted-foreground/80 max-w-xs">
              La piattaforma leader per il confronto dei prezzi e delle valutazioni dell'oro.
            </p>
            <div className="flex space-x-3">
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors">
                <Facebook className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors">
                <Instagram className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/80 transition-colors">
                <FaXTwitter className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Link Rapidi</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/grafici" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Grafici
                </Link>
              </li>
              <li>
                <Link to="/compro-oro" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Compro Oro
                </Link>
              </li>
              <li>
                <Link to="/avvisi" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Avvisi
                </Link>
              </li>
              <li>
                <Link to="/gildy" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Chat Gildy
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/registra-attivita" className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center">
                  <span className="inline-block w-2 h-2 bg-gold/50 rounded-full mr-2"></span>
                  Registra la tua Attività
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Aggiunta sezione Compro Oro per città */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Compro Oro per Città</h3>
            <div className="grid grid-cols-2 gap-1">
              {popularCities.map((city) => (
                <a 
                  key={city}
                  href={`/compro-oro/${createCitySlug(city)}`}
                  onClick={(e) => handleCityClick(city, e)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center"
                >
                  <span className="inline-block w-1.5 h-1.5 bg-gold/70 rounded-full mr-2"></span>
                  {city}
                </a>
              ))}
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-4">Contatti</h3>
            <div className="space-y-3">
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <Phone className="h-3.5 w-3.5" />
                </div>
                <span>+39 (0)6 62 288 504</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <Mail className="h-3.5 w-3.5" />
                </div>
                <span>info@gildy.it</span>
              </div>
              <div className="flex items-center text-sm text-muted-foreground group hover:text-foreground transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center mr-3 group-hover:bg-gold/10">
                  <MapPin className="h-3.5 w-3.5" />
                </div>
                <span>Via Padova, 13, 00162 Roma, Italia</span>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2 bg-card hover:bg-muted/30 border-muted-foreground/20"
              onClick={() => setShowContactModal(true)}
            >
              Richiedi Informazioni
            </Button>
          </div>
        </div>
        
        <Separator className="my-4 opacity-30" />
        
        <div className="flex flex-col md:flex-row justify-between items-center text-xs">
          <p className="text-muted-foreground/70 mb-4 md:mb-0">
            &copy; {currentYear} Gildy. Tutti i diritti riservati.
          </p>
          <div className="flex space-x-6">
            <button 
              onClick={() => setShowPrivacyModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => setShowCookieModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Cookie Policy
            </button>
            <button 
              onClick={() => setShowTermsModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Termini di Servizio
            </button>
            <button 
              onClick={() => setShowDisclaimerModal(true)}
              className="text-muted-foreground/70 hover:text-foreground transition-colors"
            >
              Disclaimer
            </button>
          </div>
        </div>
        
        <div className="text-center mt-6 text-xs text-muted-foreground/60">
          Made with <span className="text-rose-500">♥</span> by <a href="https://www.molecole.com" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">molecole</a>
        </div>
      </div>
      
      <ContactFormModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
      
      <PrivacyPolicyModal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)} 
      />
      
      <CookiePolicyModal 
        isOpen={showCookieModal} 
        onClose={() => setShowCookieModal(false)} 
      />
      
      <DisclaimerModal 
        isOpen={showDisclaimerModal} 
        onClose={() => setShowDisclaimerModal(false)} 
      />
      
      <TermsOfServiceModal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)} 
      />
    </footer>
  );
}
